import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonType,
  Checkbox,
  Select,
  Tooltip
} from 'syngenta-digital-cropwise-react-ui-kit';
import { useFormikContext } from 'formik';
import useProduct from 'hooks/useProduct';
import { getShortUnitLabel } from 'helpers/unitsAbbreviation';
import UndoIcon from 'assets/undo.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import ChevronUp from 'assets/chevron_up.svg?react';
import InfoIcon from 'assets/info.svg?react';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import FormattedNumber from 'components/FormattedNumber';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import getDropdownParentWithoutDetaching from 'screens/Applications&WorkOrders/StepsLayout/Helpers/getDropdownParentWithoutDetaching';
import { useProductCalculations } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/hooks/useProductCalculations';
import { useProductFormUnits } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/hooks/useProductFormUnits';
import FormSectionHeader from '../FormSectionHeader';
import filterOptionByNormalizedString from '../../../../../../Helpers/filterOptions';

interface Props {
  product: Product;
  className?: string;
  application?: FormValues;
}

const ProductCostSection: React.FC<Props> = ({
  className = 'mt-4 mb-6',
  product,
  application
}) => {
  const { tankCount, isTankInfoEnabled } = application || {};

  const units = useProductFormUnits(product);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { values, setFieldValue } = useFormikContext<Product>();
  // in the future will need to be deprecated to point to catalog
  const { getProduct } = useProduct();
  const { recalculateAfterPriceChange } = useProductCalculations({
    tankCount,
    isTankInfoEnabled
  });

  // Fetch list price from inventory to display and enable reset functionality
  useEffect(() => {
    if (product.id) {
      getProduct(product.id).then((data: any) => {
        if (data?.averagePricePerUnit !== undefined) {
          // Update the list price in Formik for display/reset
          setFieldValue('averagePricePerUnit', data.averagePricePerUnit);
          if (data.averagePricePerUnitUnit) {
            setFieldValue(
              'averagePricePerUnitUnit',
              data.averagePricePerUnitUnit
            );
          }
          // Only populate priceValue for NEW products (no saved priceValue)
          // For edit mode, product.isInUse is true and product.priceValue has the saved value
          // A price is considered "saved" if it's a number (including 0)
          const hasSavedPrice = typeof product.priceValue === 'number';
          // Only populate if NOT in use AND does NOT have a saved price
          const isNewProduct = !product.isInUse || !hasSavedPrice;
          if (isNewProduct) {
            setFieldValue('priceValue', data.averagePricePerUnit);
            if (data.averagePricePerUnitUnit) {
              setFieldValue('priceUnit', data.averagePricePerUnitUnit);
            }
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  useEffect(() => {
    const parsedPriceValue = Number(values.priceValue);
    const hasValidPriceValue =
      Number.isFinite(parsedPriceValue) && parsedPriceValue > 0;

    // If price value is invalid, clear the totalCost in Formik and skip calculations
    if (!hasValidPriceValue) {
      if (values.totalCost !== undefined) {
        setFieldValue('totalCost', undefined);
      }
      return;
    }

    // If we have a valid price and unit, calculate total cost
    if (values.priceUnit) {
      const result = recalculateAfterPriceChange();
      const nextTotalCost = result?.totalCostAtTimeOfCreation;

      if (nextTotalCost === undefined) {
        if (values.totalCost !== undefined) {
          setFieldValue('totalCost', undefined);
        }
      } else if (values.totalCost !== nextTotalCost) {
        setFieldValue('totalCost', nextTotalCost);
      }
    }
    // Don't reset when price values are missing - preserve initial value from product.totalCost
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.priceValue,
    values.priceUnit,
    values.totalCost,
    values.totalProduct,
    values.ratePerAcre,
    values.ratePerTank,
    values.appliedAreaValue,
    recalculateAfterPriceChange
  ]);

  // List price comes from Formik values (updated by fetch) or product prop
  // Use || instead of ?? to skip 0 values and get the actual list price
  const listPrice =
    values.averagePricePerUnit || product.averagePricePerUnit || 0;
  const listPriceUnit =
    values.averagePricePerUnitUnit || product.averagePricePerUnitUnit || '';

  const hasListPrice = listPrice > 0;

  const currentPrice = {
    value: values.priceValue || 0,
    unit: values.priceUnit
  };

  const isResetButtonDisabled =
    !hasListPrice ||
    (currentPrice.value === listPrice && currentPrice.unit === listPriceUnit);

  const canSaveNewListPrice =
    currentPrice.value !== listPrice || currentPrice.unit !== listPriceUnit;

  const handleResetPrice = () => {
    if (hasListPrice) {
      setFieldValue('saveAsNewListPrice', false);
      setFieldValue('priceValue', listPrice);
      setFieldValue('priceUnit', listPriceUnit);
    }
  };

  const resetButton = (
    <Button
      disabled={isResetButtonDisabled}
      ghost={!isResetButtonDisabled}
      type={isResetButtonDisabled ? ButtonType.link : ButtonType.primary}
      icon={<UndoIcon />}
      onClick={handleResetPrice}
      className={`p-1px text-neutral-60 border-none ml-2 ${
        isResetButtonDisabled ? 'bg-neutral-10' : ''
      }`}
      style={{ width: '33px', height: '33px' }}
    />
  );

  return (
    <section data-testid="product-cost-section" className={className}>
      <FormSectionHeader title="Cost" />

      <div className="flex items-end mb-2">
        <FormInputWithPrecisionDisplay
          label="Price per Unit"
          fieldName="priceValue"
          type="number"
          inputPrefix="$"
          inputSuffix="per"
          precision={2}
          round={2}
          onControlledBlur={(val?: string) => {
            if (!val) setFieldValue('priceValue', 0);
          }}
          addonAfter={
            <Select
              showSearch
              value={values.priceUnit}
              onChange={value => setFieldValue('priceUnit', value)}
              className="custom-select"
              onDropdownVisibleChange={visible => setIsDropdownVisible(visible)}
              suffixIcon={
                isDropdownVisible ? (
                  <ChevronUp width={18} height={18} />
                ) : (
                  <ChevronDown width={18} height={18} />
                )
              }
              getPopupContainer={getDropdownParentWithoutDetaching}
              filterOption={filterOptionByNormalizedString}
            >
              {units.map(unit => (
                <Select.Option
                  key={`priceUnit-${unit.key}`}
                  value={unit.key}
                  label={getShortUnitLabel(unit.key)}
                >
                  {getShortUnitLabel(unit.key)}
                </Select.Option>
              ))}
            </Select>
          }
        />

        {hasListPrice ? (
          <Tooltip
            title={isResetButtonDisabled ? '' : 'Reset to List Price'}
            placement="top"
          >
            {resetButton}
          </Tooltip>
        ) : (
          resetButton
        )}
      </div>

      <Checkbox
        disabled={!canSaveNewListPrice}
        name="saveAsNewListPrice"
        checked={values.saveAsNewListPrice}
        onChange={e => setFieldValue('saveAsNewListPrice', e.target.checked)}
      >
        Save as new list price
      </Checkbox>

      {values.saveAsNewListPrice && (
        <div className="flex items-center mt-2" style={{ gap: '4px' }}>
          <InfoIcon
            style={{ flexShrink: 0, width: 18, height: 18, fill: '#0092E4' }}
          />
          <span
            className="text-sm font-normal leading-5 text-neutral-90"
            style={{ letterSpacing: '-0.105px' }}
          >
            This will not change any existing records.
          </span>
        </div>
      )}

      <div className="mt-6">
        <span className="block text-sm font-medium text-neutral-60 mb-2">
          Total Cost
        </span>
        <div className="text-base font-normal text-black">
          {values.totalCost ? (
            <FormattedNumber unit="usCurrency">
              {values.totalCost}
            </FormattedNumber>
          ) : (
            '$ ---'
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductCostSection;
